import { PaymentMethodEnum, type ExcludeOtherPaymentMethod } from '@/types/common';
import type {
  Appearance,
  CreatePaymentMethodData,
  StripeElements,
  StripeExpressCheckoutElementOptions,
} from '@stripe/stripe-js';

export const STRIPE_CONFIG = { developerTools: { assistant: { enabled: false } } };

export const STRIPE_APPEARANCE: Appearance = {
  theme: 'stripe',
  variables: {
    colorPrimary: '#0066cc',
    colorBackground: '#ffffff',
    colorText: '#30313d',
    colorDanger: '#df1b41',
    fontFamily: 'sans-serif',
    spacingUnit: '4px',
    borderRadius: '6px',
    fontSizeBase: '14px',
  },
  rules: {
    '.Input': {
      border: '1px solid #e0e0e0',
      padding: '12px 8px',
      fontSize: '14px',
    },
    '.Input:focus': {
      border: '1px solid #e0e0e0',
      boxShadow: 'none',
    },
    '.Label': {
      fontSize: '0px',
      lineHeight: '0',
    },
    '.Error': {
      fontSize: '0px',
      lineHeight: '0',
    },
  },
};

export const PaymentMethodsToStripe: Record<ExcludeOtherPaymentMethod, string> = {
  [PaymentMethodEnum.CREDIT_CARD]: 'card',
  [PaymentMethodEnum.APPLE_PAY]: 'apple',
  [PaymentMethodEnum.GOOGLE_PAY]: 'google',
  [PaymentMethodEnum.PAYPAL]: 'paypal',
  [PaymentMethodEnum.CASH_APP]: 'cashapp',
  [PaymentMethodEnum.KLARNA]: 'klarna',
  [PaymentMethodEnum.LINK]: 'link',
  [PaymentMethodEnum.UNIONPAY]: 'unionpay',
  [PaymentMethodEnum.IDEAL]: 'ideal',
  [PaymentMethodEnum.BANCONTACT]: 'bancontact',
  [PaymentMethodEnum.EPS]: 'eps',
  [PaymentMethodEnum.P24]: 'p24',
  [PaymentMethodEnum.BLIK]: 'blik',
  [PaymentMethodEnum.AFTERPAY]: 'afterpay_clearpay',
  [PaymentMethodEnum.AFFIRM]: 'affirm',
  [PaymentMethodEnum.AMAZON_PAY]: 'amazon_pay',
  [PaymentMethodEnum.VENMO]: 'venmo',
  [PaymentMethodEnum.ALIPAY]: 'alipay',
  [PaymentMethodEnum.VERIFON]: 'verifone',
  [PaymentMethodEnum.PIX]: 'pix',
  [PaymentMethodEnum.NEOSURF]: 'neosurf',
  [PaymentMethodEnum.NUVEI_NEOSURF]: 'neosurf',
  [PaymentMethodEnum.BLIK_CHECKOUT]: 'blik',
  [PaymentMethodEnum.TRUSTLY]: 'trustly',
  [PaymentMethodEnum.PAYME]: 'payme',
};

export const LINK_PAYMENT_CONFIG: StripeExpressCheckoutElementOptions = {
  paymentMethods: {
    link: 'auto',
    applePay: 'never',
    googlePay: 'never',
  },
};

export const GOOGLE_PAY_PAYMENT_CONFIG: StripeExpressCheckoutElementOptions = {
  paymentMethods: {
    googlePay: 'always',
    link: 'never',
    applePay: 'never',
  },
};

export const APPLE_PAY_PAYMENT_CONFIG: StripeExpressCheckoutElementOptions = {
  paymentMethods: {
    applePay: 'always',
    link: 'never',
    googlePay: 'never',
  },
};

export const AMAZON_PAY_PAYMENT_CONFIG: StripeExpressCheckoutElementOptions = {
  paymentMethods: {
    amazonPay: 'auto',
    link: 'never',
    googlePay: 'never',
    applePay: 'never',
  },
};

export const paymentMethodRequireBank: PaymentMethodEnum[] = [
  PaymentMethodEnum.P24,
  PaymentMethodEnum.EPS,
  PaymentMethodEnum.BANCONTACT,
  PaymentMethodEnum.AFTERPAY,
];

export const StripeParamsByPaymentMethod: {
  [key in PaymentMethodEnum]?: (
    params: Record<string, unknown>
  ) => CreatePaymentMethodData & { elements?: StripeElements };
} = {
  [PaymentMethodEnum.P24]: (params: Record<string, unknown>) => {
    return {
      type: 'p24',
      billing_details: {
        email: params.email as string,
      },
      p24: {
        bank: 'blik',
      },
    };
  },
  [PaymentMethodEnum.BLIK_CHECKOUT]: (params: Record<string, unknown>) => {
    return {
      type: 'p24',
      billing_details: {
        email: params.email as string,
      },
      p24: {
        bank: 'blik',
      },
    };
  },
  [PaymentMethodEnum.CASH_APP]: (params: Record<string, unknown>) => {
    return {
      type: 'cashapp',
      billing_details: {
        email: params.email as string,
      },
    };
  },
  [PaymentMethodEnum.EPS]: (params: Record<string, unknown>) => {
    return {
      type: 'eps',
      billing_details: {
        email: params.email as string,
        name: (params.name as string) || '',
      },
      eps: {},
      elements: params.elements as StripeElements,
    };
  },
  [PaymentMethodEnum.BANCONTACT]: (params: Record<string, unknown>) => {
    return {
      type: 'bancontact',
      billing_details: {
        email: params.email as string,
        name: (params.name as string) || '',
      },
      bancontact: {},
      elements: params.elements as StripeElements,
    };
  },
  [PaymentMethodEnum.AFTERPAY]: (params: Record<string, unknown>) => {
    return {
      type: 'afterpay_clearpay',
      billing_details: {
        email: params.email as string,
      },
      elements: params.elements as StripeElements,
    };
  },
};

export const GOOGLE_PAY_WALLET = 'google_pay';
export const APPLE_PAY_WALLET = 'apple_pay';
export const LINK_PAYMENT_WALLET = 'link';
